import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../../components/seo/SEOHead';
import { FAQSchema, BreadcrumbSchema } from '../../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../../components/seo/Breadcrumbs';
import { LatestArticles } from '../../components/blog/LatestArticles';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { Badge } from '../../components/ui/badge';
import { Calendar, Music, Users, Zap, ArrowRight } from 'lucide-react';

export default function DJNightJamshedpur() {
  const navigate = useNavigate();

  const faqs = [
    {
      question: "When are DJ nights held at Scratch Unleashed?",
      answer: "We host DJ nights every Friday, Saturday, and Sunday from 6 PM onwards. Special DJ events and guest performances are scheduled throughout the month. Check our events calendar for lineup announcements and guest DJ appearances. We're also available for private DJ nights on weekdays."
    },
    {
      question: "What music genres do the DJs play?",
      answer: "Our resident and guest DJs specialize in house, techno, hip-hop, commercial EDM, and Bollywood remixes. We curate our lineup to offer variety throughout the week, with different genres featured each night to keep the energy fresh and exciting."
    },
    {
      question: "What is the cover charge for DJ nights?",
      answer: "Cover charges for DJ nights range from ₹1500-₹3000 depending on the event and artist. Regular weekend DJ nights start at ₹1500 per person, while special guest DJ performances range from ₹2000-₹3000. VIP table bookings include entry for your group."
    },
    {
      question: "Can I book a table for DJ night events?",
      answer: "Absolutely! Table reservations are highly recommended for DJ nights as they sell out quickly. You can book through our website or call our reservations team. We offer standard tables, VIP booths, and premium stage-side tables with bottle service."
    },
    {
      question: "Are there any age restrictions for DJ nights?",
      answer: "Yes, all DJ night events at Scratch Unleashed are strictly 21+ only. Valid government-issued photo ID is mandatory for entry. We enforce this policy strictly to maintain a mature, sophisticated atmosphere."
    }
  ];

  const breadcrumbItems = [
    { name: 'DJ Nights in Jamshedpur', url: null }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="DJ Night in Jamshedpur | Best DJ Events at Scratch Unleashed"
        description="Experience electrifying DJ nights in Jamshedpur at Scratch Unleashed. World-class DJs, premium sound systems, VIP tables. Every Thu-Sun from 10 PM. Book now!"
        keywords="DJ night Jamshedpur, best DJ events Jamshedpur, techno night Jamshedpur, house music Jamshedpur, hip hop night Jamshedpur, EDM party Jamshedpur, DJ club Jamshedpur"
        canonical="https://scratchjsr.com/dj-night-jamshedpur"
      />
      <FAQSchema faqs={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'DJ Nights in Jamshedpur', url: 'https://scratchjsr.com/dj-night-jamshedpur' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={breadcrumbItems} />

        {/* Hero Section */}
        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            DJ NIGHT IN JAMSHEDPUR
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Where Sound Meets Soul. Every Weekend.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
              Book Your Entry
              <ArrowRight size={16} className="ml-2" />
            </Button>
            <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/events')}>
              View This Week's Lineup
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            {/* Introduction */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Jamshedpur's Premier DJ Night Destination</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  When it comes to <strong>DJ nights in Jamshedpur</strong>, Scratch Unleashed has redefined what it means to experience world-class electronic music in the city. Every Friday through Sunday, from 6 PM until late into the night, we transform into the epicenter of Jamshedpur's nightlife, bringing you the finest DJs, cutting-edge sound systems, and an atmosphere that rivals any metropolitan club.
                </p>
                <p>
                  As the <strong>best DJ night venue in Jamshedpur</strong>, we've invested heavily in creating an authentic club experience. Our Funktion-One sound system delivers pristine audio quality that lets you experience every frequency exactly as the artist intended. From deep basslines that you feel in your chest to crystal-clear highs that cut through the mix, our sound engineering ensures an immersive sonic experience that's second to none in the region.
                </p>
              </div>
            </section>

            {/* Music Programming */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Diverse Musical Journey</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  What makes our <strong>DJ events in Jamshedpur</strong> apart is our commitment to musical diversity and quality. We don't believe in one-size-fits-all programming. Instead, we've carefully curated each weekend night to cater to different musical tastes while maintaining the highest standards of DJ talent.
                </p>
                <p>
                  <strong>Friday nights</strong> kick off the weekend with high-energy commercial house, festival-style EDM, and hip-hop crossover hits. These are the nights where the energy peaks early and stays high until closing. Expect big drops, crowd singalongs, and a packed dance floor where everyone is there to celebrate.
                </p>
                <p>
                  <strong>Saturday nights</strong> bring our biggest lineups and most diverse programming. Our resident and guest DJs deliver sets spanning house, techno, and progressive sounds, creating a journey from warm-up grooves to peak-time energy. It's the night for true electronic music enthusiasts who appreciate the art of DJing.
                </p>
                <p>
                  <strong>Sunday sessions</strong> offer something unique—a fusion of Bollywood remixes, commercial hits, and feel-good anthems. It's a celebration of both global and local sounds, perfect for those who want the DJ night experience with a familiar musical flavor. Our DJs seamlessly blend international bangers with desi favorites, creating a vibe that's uniquely Scratch Unleashed.
                </p>
                <p>
                  Looking to host a private DJ night on a weekday? We're available Monday through Thursday for exclusive private events. Whether it's a corporate celebration or a private party, we can arrange a custom DJ experience tailored to your group.
                </p>
              </div>
            </section>

            {/* The Experience */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">More Than Just Music</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  A great <strong>DJ night in Jamshedpur</strong> requires more than just good music—it demands the perfect combination of sound, lighting, crowd energy, and atmosphere. At Scratch Unleashed, we've mastered this formula.
                </p>
                <p>
                  Our lighting design is choreographed to complement the music, with programmable LED systems, strobes, lasers, and atmospheric effects that transform throughout the night. During buildups, the lighting pulls back, creating anticipation. At the drop, the entire venue explodes with synchronized visual effects that amplify the sonic impact.
                </p>
                <p>
                  The crowd at our DJ nights is equally important. Our selective door policy and reputation have cultivated a community of genuine music lovers—people who come to dance, celebrate, and lose themselves in the music. You won't find phones-in-the-air crowds here; our guests are too busy experiencing the moment to document it.
                </p>
              </div>
            </section>

            {/* Guest DJs & Special Events */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Guest DJ Performances</h2>
              <div className="space-y-4 text-[var(--text-dim)] leading-relaxed">
                <p>
                  While our resident DJs consistently deliver exceptional nights, we regularly elevate the experience by bringing in nationally and internationally recognized artists. These <strong>special DJ events in Jamshedpur</strong> have featured artists who've played at Tomorrowland, Ultra Music Festival, Sunburn, and other major festivals worldwide.
                </p>
                <p>
                  When we announce a guest DJ performance, it becomes the talk of the city. These events sell out quickly, with music fans traveling from nearby cities to experience world-class DJing in Jamshedpur. For these special nights, we go all out—enhanced production, extended hours, and exclusive after-parties for VIP ticket holders.
                </p>
              </div>
            </section>

            {/* VIP Experience */}
            <section>
              <h2 className="font-bebas text-3xl mb-4">Elevate Your DJ Night Experience</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">VIP Table Service</h3>
                  <p className="text-[var(--text-dim)]">For those who want the ultimate DJ night experience, our VIP table service offers the perfect vantage point. Located strategically around the dance floor and DJ booth, our VIP tables provide the best views and sound while offering the comfort of your private space. Each table comes with dedicated bottle service, a personal host, and priority entry—no waiting in line.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">Premium Bottle Service</h3>
                  <p className="text-[var(--text-dim)]">Our bottle service menu features premium spirits, champagne, and signature mixers. Your dedicated server ensures your glasses are always full, allowing you to focus entirely on the music and your crew. We also offer custom bottle presentations for special celebrations.</p>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2 text-[var(--text)]">Stage-Side Tables</h3>
                  <p className="text-[var(--text-dim)]">For the ultimate proximity to the DJ, our stage-side tables put you right in the action. Feel the bass, watch the DJ work, and be part of the energy that drives the entire room. These premium spots are limited and book out weeks in advance for major events.</p>
                </div>
              </div>
            </section>

            {/* Call to Action */}
            <section className="bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl p-8">
              <h2 className="font-bebas text-3xl mb-4">Experience The Best DJ Nights in Jamshedpur</h2>
              <p className="text-[var(--text-dim)] mb-6">
                Whether you're a techno purist, house music devotee, or just love dancing to great music, Scratch Unleashed delivers DJ nights that will exceed your expectations. Don't just hear about it—experience it yourself.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="btn-crimson-glow rounded-xl" onClick={() => navigate('/checkin')}>
                  Reserve Your Spot
                </Button>
                <Button variant="outline" className="rounded-xl border-[var(--border-custom)]" onClick={() => navigate('/vip-table-booking-jamshedpur')}>
                  Book VIP Table
                </Button>
              </div>
            </section>

            {/* FAQ Section */}
            <section>
              <h2 className="font-bebas text-3xl mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border border-[var(--border-custom)] rounded-xl px-4 bg-[var(--surface)]">
                    <AccordionTrigger className="hover:no-underline text-left">
                      <span className="font-semibold">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-[var(--text-dim)] pb-4">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Weekly Schedule Card */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Weekly Schedule</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Calendar size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Friday</p>
                    <p className="text-sm text-[var(--text-dim)]">Commercial House & EDM</p>
                    <Badge className="mt-1 text-xs">6 PM - Late</Badge>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Saturday</p>
                    <p className="text-sm text-[var(--text-dim)]">House, Techno & Hip-Hop</p>
                    <Badge className="mt-1 text-xs">6 PM - Late</Badge>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Sunday</p>
                    <p className="text-sm text-[var(--text-dim)]">Bollywood Fusion</p>
                    <Badge className="mt-1 text-xs">6 PM - Late</Badge>
                  </div>
                </div>
                <div className="pt-2 border-t border-[var(--border-custom)]">
                  <p className="text-xs text-[var(--text-muted)]">Mon-Thu: Private events only</p>
                </div>
              </div>
            </Card>

            {/* Quick Stats */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Why Choose Us</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Music size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Funktion-One Sound</p>
                    <p className="text-sm text-[var(--text-dim)]">World-class audio quality</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Users size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Guest DJs</p>
                    <p className="text-sm text-[var(--text-dim)]">National & international artists</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Zap size={20} className="text-[var(--accent-gold)] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Premium Production</p>
                    <p className="text-sm text-[var(--text-dim)]">Lights, lasers, visual effects</p>
                  </div>
                </div>
              </div>
            </Card>


            {/* Latest Articles */}
            <LatestArticles maxPosts={3} />

            {/* Related Pages */}
            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-xl">
              <h3 className="font-bebas text-2xl mb-4">Explore More</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/night-club-in-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Night Club in Jamshedpur
                </button>
                <button
                  onClick={() => navigate('/vip-table-booking-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → VIP Table Booking
                </button>
                <button
                  onClick={() => navigate('/birthday-party-venue-jamshedpur')}
                  className="w-full text-left text-sm text-[var(--accent-gold)] hover:underline"
                >
                  → Birthday Party Venue
                </button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
